import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * EditCustomerDialog
 *
 * Quick edit for the basic customer fields (Name, Mobile, Address, Notes).
 * Credentials / advance balance are intentionally NOT exposed here — those
 * have dedicated dialogs / flows.
 *
 * Hits `PUT /api/customers/{id}`. Preserves the customer's saved credentials
 * by reading them from the server first and replaying them on save so the
 * untouched `credentials` array isn't wiped by this lightweight edit.
 */
export default function EditCustomerDialog({ open, onOpenChange, customerId, onSaved }) {
  const [form, setForm] = useState({ name: '', phone: '', address: '', notes: '' });
  const [credentials, setCredentials] = useState([]);
  const [saving, setSaving] = useState(false);
  const [confirmDup, setConfirmDup] = useState(null);

  useEffect(() => {
    if (!open || !customerId) return;
    setForm({ name: '', phone: '', address: '', notes: '' });
    setCredentials([]);
    api.get(`/customers/${customerId}`)
      .then(r => {
        const d = r.data || {};
        setForm({
          name: d.name || '',
          phone: d.phone || '',
          address: d.address || '',
          notes: d.notes || '',
        });
        // Cache existing credentials so we replay them verbatim on save —
        // this dialog must NOT clobber the customer's saved login IDs.
        setCredentials(d.credentials || []);
      })
      .catch(() => toast.error('Failed to load customer'));
  }, [open, customerId]);

  const submit = async (force = false) => {
    if (!form.phone || !form.name) { toast.error('Mobile and name required'); return; }
    setSaving(true);
    try {
      await api.put(`/customers/${customerId}${force ? '?force=true' : ''}`, {
        name: form.name,
        phone: form.phone,
        address: form.address,
        notes: form.notes,
        credentials,
      });
      toast.success('Customer updated');
      onOpenChange(false);
      setConfirmDup(null);
      onSaved?.();
    } catch (err) {
      if (err.response?.status === 409) {
        setConfirmDup(err.response.data.detail || 'Duplicate customer detected. Save anyway?');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to update customer');
      }
    } finally { setSaving(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-2xl" data-testid="edit-customer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={18} className="text-[#3B82F6]" />
              Edit Customer
            </DialogTitle>
            <DialogDescription>Update the customer's basic details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Mobile *</Label>
                <Input
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="rounded-lg mt-1"
                  data-testid="ecd-phone"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Name *</Label>
                <Input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="rounded-lg mt-1"
                  data-testid="ecd-name"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Address</Label>
              <Input
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
                className="rounded-lg mt-1"
                data-testid="ecd-address"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                rows={2}
                placeholder="Anything to remember about this customer..."
                className="rounded-lg mt-1 resize-none"
                data-testid="ecd-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl" data-testid="ecd-cancel">
              Cancel
            </Button>
            <Button
              onClick={() => submit(false)}
              disabled={saving}
              className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl disabled:opacity-50"
              data-testid="ecd-save"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDup} onOpenChange={(v) => { if (!v) setConfirmDup(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Customer</AlertDialogTitle>
            <AlertDialogDescription>{confirmDup}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => submit(true)}
              className="bg-[#3B82F6] text-white hover:bg-[#2563EB] rounded-lg"
              data-testid="ecd-confirm-dup"
            >
              Save Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
